import { logger, sanitizeError } from '@heidi/runtime/logger';
import type { QueryClient } from '@tanstack/react-query';

import { agentHarnessKeys } from '../../../contracts/keys';
import { cancelTurn } from '../../api/harness';

/**
 * Cancel the CHILD run's in-flight turn — never the parent: the BE posts the
 * cancelled tool result on the parent stream, which stays alive to react.
 * Deliberately no local executor stop (a plain child run owns none). Rejects
 * on BE failure so the caller can clear its "Stopping" latch for retry.
 *
 * The cache is the caller's: a timeline row's Stop runs outside a render, so
 * the app hands over the client its provider holds. The `[rpa]` log tag is kept
 * from this helper's earlier home beside the computer-use cancel, so existing
 * log queries still find it.
 */
export const cancelChildRun = async (
  childAgentRunId: string,
  queryClient: QueryClient
): Promise<void> => {
  try {
    await cancelTurn(childAgentRunId);
  } catch (error: unknown) {
    logger.warn('[rpa] cancel child run failed', {
      agent_run_id: childAgentRunId,
      error: sanitizeError(error),
    });
    throw error;
  }
  // The .catch matters: a bare `void` would turn a failed invalidation into
  // an unhandled rejection.
  void queryClient
    .invalidateQueries({
      queryKey: agentHarnessKeys.run(childAgentRunId),
    })
    .catch((error: unknown) => {
      logger.warn('[rpa] invalidate child run after cancel failed', {
        agent_run_id: childAgentRunId,
        error: sanitizeError(error),
      });
    });
};
